import { useCallback, useState, useMemo, useEffect } from 'react';

import { useMutation } from '@tanstack/react-query';

import { getUsersAdministration } from '../api';
import { Criteria, PageParams, SortType } from '../types/pagination';

export const useUsers = () => {
  const [lastParams, setLastParams] = useState<PageParams | null>(null);

  const mutation = useMutation({
    mutationKey: ['getUsersAdministration'],
    mutationFn: (params: PageParams) => {
      setLastParams(params);
      return getUsersAdministration(params);
    },
  });

  const retryLast = useCallback(() => {
    if (lastParams !== null) {
      mutation.mutate(lastParams);
    }
  }, [lastParams, mutation]);

  return { ...mutation, retryLast };
};

type AuthorOption = {
  id: number;
  name: string;
};

export const useAllUsers = () => {
  const [allUsers, setAllUsers] = useState<AuthorOption[]>([]);

  const mutation = useMutation({
    mutationKey: ['getAllUsers'],
    mutationFn: (params: PageParams) => getUsersAdministration(params),
  });

  const { mutate } = mutation;

  useEffect(() => {
    mutate({
      pageNumber: 0,
      pageSize: 1000,
      sortFieldName: 'name',
      sortDirection: SortType.Ask,
      filters: [
        {
          fieldName: 'role',
          filterCriteria: Criteria.Equals,
          matches: ['Manager'],
        },
      ],
    });
  }, [mutate]);

  useEffect(() => {
    if (mutation.data?.result?.pageItems) {
      setAllUsers(
        mutation.data.result.pageItems
          .map((u) => ({
            id: u.id,
            name: u.displayName || `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim(),
          }))
          .filter((u) => u.name !== ''),
      );
    }
  }, [mutation.data]);

  const authors = useMemo(() => allUsers, [allUsers]);

  return {
    authors,
    isLoading: mutation.isPending,
    refetch: () =>
      mutation.mutate({
        pageNumber: 0,
        pageSize: 1000,
        sortFieldName: 'name',
        sortDirection: SortType.Ask,
        filters: [
          {
            fieldName: 'role',
            filterCriteria: Criteria.Equals,
            matches: ['Manager'],
          },
        ],
      }),
  };
};
